import { useEffect, useMemo, useState } from 'react';
import { fetchVitalSignResult, fetchVitalSigns, IVitalSign, selectVitalSigns, updateVitalSingByUserProfile } from '../../../store/slices/vitalSigns';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { useTranslation } from '../../../hooks/useTranslations';
import { toast } from 'react-toastify';
import { getCurrentTimeWithMicroseconds } from '../../../utils/miscUtils';
import CustomTextField from '../../../components/UI/Inputs/CustomTextField';
import CustomDropdown from '../../../components/UI/Inputs/CustomDropdown';
import _ from 'lodash';
import moment from 'moment';
import styles from './ManualRecord.module.scss';

interface Props {
    group: { groupName: string; groupId: number }
    accountNumber: string | undefined;
    personId?: number;
    setReport: (group: { groupName: string; groupId: number }, data: Array<IVitalSign>) => void;
}

interface validationType {
    [key: string]: boolean;
}
interface IsConfirmedValidationTypes {
    validation: validationType;
}

const ManualRecord = ({ group, accountNumber, personId, setReport }: Props) => {
    const dispatch = useAppDispatch();
    const { translations } = useTranslation();
    const [dynamicInput, setDynamicInput] = useState<any>({});
    const vitalSigns = useAppSelector(selectVitalSigns);

    // const disableButton = Object.entries(dynamicInput)
    //     .filter(([key, value]) => key !== 'date' && key !== 'time')
    //     .every(([key, value]) => value === '');

    const [isConfirmedValidation, setIsConfirmedValidation] = useState<IsConfirmedValidationTypes>({
        validation: {},
    });

    const glucoseMealTimeOption = [
        { id: 1, label: translations(`vitalsigns.mealTime.1`) },
        { id: 2, label: translations('vitalsigns.mealTime.2') },
        { id: 3, label: translations('vitalsigns.mealTime.3') },
    ];

    const glucoseUnitOption = [
        { id: 1, label: 'mg/dL' },
        { id: 7, label: 'mmol/L' },
    ];

    const updatedVitalSigns = useMemo(() => {
        if (vitalSigns.length === 0) return [];
        return [...vitalSigns?.filter((el: IVitalSign) => el.services),
        // These are hardcoded values, because the API does not return them
        {
            vital_sign_name: 'Mealtime',
            vital_sign_id: '',
            vital_sign_group_name: 'Blood Glucose',
            vital_sign_group_id: '5',
            biomarker_uom: '',
            account_vital_sign_external_id: '',
            vital_sign_uom_id: '',
            vital_sign_references: [],
            maximum_value: 0,
            minimum_value: 0,
            services: [
                { service_id: '', service_name: 'Online Consulting', is_mandatory: true },
                { service_id: '', service_name: 'Healthcheck Consultation', is_mandatory: true },
                { service_id: '', service_name: 'Account Vital Signs', is_mandatory: true },
            ],
        },
        {
            vital_sign_name: 'Unit',
            vital_sign_id: '',
            vital_sign_group_name: 'Blood Glucose',
            vital_sign_group_id: '5',
            biomarker_uom: '',
            account_vital_sign_external_id: '',
            vital_sign_uom_id: '',
            vital_sign_references: [],
            services: [
                { service_id: '', service_name: 'Online Consulting', is_mandatory: true },
                { service_id: '', service_name: 'Healthcheck Consultation', is_mandatory: true },
                { service_id: '', service_name: 'Account Vital Signs', is_mandatory: true },
            ],
        }]
    }, [vitalSigns]);

    const items = useMemo(() => {
        return _.groupBy(updatedVitalSigns, 'vital_sign_group_id');
    }, [updatedVitalSigns]);

    useEffect(() => {
        if (vitalSigns.length > 0) return
        dispatch(fetchVitalSigns())
    }, []);

    const validateVitalSigns = (): { isValid: boolean; hasMissingMandatory: boolean; hasOutOfRange: boolean } => {
        let validation: validationType = {};
        let isValid = true;
        let hasMissingMandatory = false;
        let hasOutOfRange = false;

        items[group.groupId].forEach((el) => {
            const value = dynamicInput[el.vital_sign_name];
            let minimumValue;
            let maximumValue;
            if (el.vital_sign_name === 'Blood Glucose') {
                const obj = el.vital_sign_references.find(
                    (el: any) =>
                        el.mealtime_id === (Number(dynamicInput.Mealtime) ?? null) &&
                        el.biomarker_uom_id === (Number(dynamicInput.Unit) ?? null)
                );
                minimumValue = obj?.minimum_value;
                maximumValue = obj?.maximum_value;
            } else {
                minimumValue = el?.vital_sign_references?.[0]?.minimum_value;
                maximumValue = el?.vital_sign_references?.[0]?.maximum_value;
            }

            // Blood Glucose VitalSign, all inputs will be mandatory
            const isMandatory = el.vital_sign_group_name === 'Blood Glucose' ? true : false

            if (!value && !isMandatory) {
                validation[el.vital_sign_name] = false;
            } else if (!value && isMandatory) {
                validation[el.vital_sign_name] = true;
                isValid = false;
                hasMissingMandatory = true;
            } else if (minimumValue !== null && maximumValue !== null) {
                if (Number(value) < Number(minimumValue) || Number(value) > Number(maximumValue)) {
                    validation[el.vital_sign_name] = true;
                    isValid = false;
                    hasOutOfRange = true;
                } else {
                    validation[el.vital_sign_name] = false;
                }
            } else {
                validation[el.vital_sign_name] = false;
            }
        });

        setIsConfirmedValidation((prevState: IsConfirmedValidationTypes) => ({
            ...prevState,
            validation: validation,
        }));

        return { isValid, hasMissingMandatory, hasOutOfRange };
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setDynamicInput((prevState: any) => ({
            ...prevState,
            [name]: value,
        }));

        let validation: validationType = {};
        const currentVitalSign = items[group.groupId].find((el) => el.vital_sign_name === name);
        if (currentVitalSign) {
            let minimum_value;
            let maximum_value;
            if (currentVitalSign.vital_sign_name === 'Blood Glucose') {
                const obj = currentVitalSign.vital_sign_references.find(
                    (el: any) =>
                        el.mealtime_id === (Number(dynamicInput.Mealtime) ?? null) &&
                        el.biomarker_uom_id === (Number(dynamicInput.Unit) ?? null)
                );
                minimum_value = obj?.minimum_value;
                maximum_value = obj?.maximum_value;
            } else {
                minimum_value = currentVitalSign?.vital_sign_references?.[0]?.minimum_value;
                maximum_value = currentVitalSign?.vital_sign_references?.[0]?.maximum_value;
            }
            if (!value) {
                validation[name] = false;
            } else if (minimum_value !== null && maximum_value !== null) {
                if (Number(value) < Number(minimum_value) || Number(value) > Number(maximum_value)) {
                    validation[name] = true;
                } else {
                    validation[name] = false;
                }
            } else {
                validation[name] = false;
            }
        }

        setIsConfirmedValidation((prevState: IsConfirmedValidationTypes) => ({
            ...prevState,
            validation: { ...prevState.validation, ...validation },
        }));
    };

    const handleSubmit = async () => {
        const { isValid, hasMissingMandatory, hasOutOfRange } = validateVitalSigns();
        if (!isValid) {
            const message = hasOutOfRange
                ? translations('vitalsigns.error.out_of_range')
                : hasMissingMandatory
                    ? translations('vitalsigns.error.missing_values')
                    : translations('vitalsigns.error.invalid_value');

            toast.error(message, {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            return;
        }

        const vitalSignsData = Object.entries(dynamicInput).map(([key, value]) => {
            const item = items[group.groupId].find((item: any) => item.vital_sign_name === key)
            if (key === 'date' || key === 'time' || item?.vital_sign_name === 'Mealtime' || item?.vital_sign_name === 'Unit') return null;
            if (!value) return null;
            if (key === 'Blood Glucose') {
                return {
                    property_id: Number(item?.account_vital_sign_external_id),
                    property_name: key,
                    property_value: Number(value),
                    property_uom_id: Number(dynamicInput['Unit']),
                    mealtime_id: Number(dynamicInput['Mealtime']),
                }
            } if (key === 'Body Weight') {
                return {
                    property_id: Number(item?.account_vital_sign_external_id),
                    property_name: key,
                    property_value: Number(value),
                    property_uom_id: Number(updatedVitalSigns.find((el: any) => el.vital_sign_name === 'BMI Classification')?.vital_sign_uom_id),
                    type_of_vital: "BMI",
                }
            }
            return {
                property_id: Number(item?.account_vital_sign_external_id),
                property_name: key,
                property_value: Number(value),
                property_uom_id: Number(item?.vital_sign_uom_id),
            }
        }).filter((el) => el !== null);

        const data = {
            vital_sign_result: vitalSignsData,
            datetime_measure: getCurrentTimeWithMicroseconds()
        }

        await dispatch(updateVitalSingByUserProfile({ data: data, account_number: accountNumber })).unwrap()
            .then(() => {
                toast.success(translations('vitalsigns.successfully.updated'), {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })

        await dispatch(fetchVitalSignResult({
            account_number: accountNumber,
            vital_sign_group_id_out: group.groupId
        })).unwrap()
            .then((res) => {
                const data = _.groupBy(res.data, 'vital_sign_group_id_out');
                const groupedByDate: Record<string, any[]> = _.groupBy(data[group.groupId],
                    (item) => moment(item.datetime_measure).format('DD.MM.YYYY - HH:mm'))
                const lastItems = Object.entries(groupedByDate).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())[0][1];
                setReport(group, lastItems)
            })
    }

    return (
        <div className={styles.manualRecordContainer}>
            <div className={styles.wrapper}>
                <CustomTextField
                    label={translations('vitalsigns.date') ?? ''}
                    value={moment().format('DD.MM.yyyy')}
                    onChange={handleInput}
                    name={'date'}
                    disabled
                    maxWidth='300px'
                />
                <CustomTextField
                    label={translations('vitalsigns.time') ?? ''}
                    value={moment().format('HH:mm')}
                    onChange={handleInput}
                    name={'time'}
                    disabled
                    maxWidth='300px'
                />
            </div>

            <div className={styles.wrapper}>
                {items[group.groupId] &&
                    items[group.groupId].map((item, idx) => {
                        const isInValid = isConfirmedValidation.validation[item.vital_sign_name];
                        // These vital signs are from Blood Glucose group are not returned from the API, thats why we need to handle them differently
                        if (item.vital_sign_name === 'Mealtime' || item.vital_sign_name === 'Unit') {
                            return (
                                <CustomDropdown
                                    errorValidation={isInValid}
                                    placeholder="Select"
                                    label={
                                        item.vital_sign_name === 'Mealtime'
                                            ? translations('vitalsigns.glucose.mealtime')
                                            : translations('vitalsigns.glocose.unit')
                                    }
                                    name={item.vital_sign_name}
                                    value={
                                        item.vital_sign_name === 'Mealtime'
                                            ? dynamicInput['Mealtime']
                                            : dynamicInput['Unit']
                                    }
                                    onChange={handleInput}
                                    options={
                                        item.vital_sign_name === 'Mealtime'
                                            ? glucoseMealTimeOption
                                            : glucoseUnitOption
                                    }
                                    key={idx}
                                />
                            );
                        }

                        return (
                            <CustomTextField
                                key={idx}
                                unit={
                                    item.vital_sign_group_name === 'Blood Glucose'
                                        ? glucoseUnitOption.find((el) => el.id == dynamicInput['Unit'])?.label
                                        : item.biomarker_uom
                                }
                                label={item.vital_sign_name}
                                value={dynamicInput[item.vital_sign_name]?.toString()}
                                type="number"
                                name={item.vital_sign_name}
                                onChange={handleInput}
                                maxWidth="300px"
                                errorValidation={isInValid}
                            />
                        );
                    })}
            </div>

            <div className={styles.btnWrapper}>
                <button className={styles.btn} onClick={handleSubmit}>
                    {translations("button.save")}
                </button>
            </div>
        </div>
    );
}

export default ManualRecord;